'use client';

import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useFormState } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { ROOT_PORTALS } from 'constants/targets';
import { Portal, Drawer, Conditional } from 'components/pcc-components';
import { useMyEquipmentDrawer } from '../my-equipment-drawer-context';
import { addEquipment } from 'services/equipment/actions';
import { useQuickToast } from 'hooks/use-toast';
import { useTranslations } from 'next-intl';
import { useParams, usePathname } from 'next/navigation';
import { RegisterSignInButtonToast } from '../equipment-toast/register-signin-button-toast';
import { capitalizeText } from '@cat-ecom/common/utils/stringUtils';
import {
  DUPLICATE_EQUIPMENT_ERROR,
  FORM_FIELD_NICK_NAME,
  GENERIC_ERROR,
  NOTIFY_DEALER,
  SELECTED_EQUIPMENT,
  SHARE_EQUIPMENT_DEALER_EMAIL_ERROR,
  UNKNOWN_ERROR_MESSAGE
} from '../constants';
import type { Equipment } from 'services/equipment-search/types';
import type {
  AddEquipmentDataForm,
  AddEquipmentState
} from 'services/equipment/types';
import { useFeedback } from 'providers/feedback-provider';
import {
  FORM_SAVE_EQUIPMENT,
  NETWORK_ERROR,
  SAVE_EQUIPMENT,
  STATUS_FAIL,
  STATUS_SUCCESS
} from 'constants/analyticsConstants';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { addEquipmentToList } from 'services/savedLists/actions';
import { HEADLESS_SAVED_LISTS } from 'constants/links';
import { DealerNotifyFailureToast } from './dealer-notify-failure-toast';

export const AddEquipmentDrawerWrapper: FC<
  PropsWithChildren<{
    isLoggedIn?: boolean;
    renderWithPortals?: boolean;
    storeId?: string;
    defaultAddEquipment?: boolean;
    isAddEquipmentToastCookie?: boolean;
  }>
> = ({
  children,
  isLoggedIn,
  renderWithPortals = true,
  storeId,
  defaultAddEquipment = true,
  isAddEquipmentToastCookie = false
}) => {
  const { updateFeedback } = useFeedback();
  const t = useTranslations();
  const pathname = usePathname();
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // savedListId should always be a string containing just numbers.
  // This is being ensured in list-details-table-body.tsx, doing Number(params.savedListId).
  const params = useParams();

  const [showDealerNotifyToasts, setShowDealerNotifyToasts] = useState(false);
  const [showAddEquipSuccessToast, setShowAddEquipSuccessToast] =
    useState(false);
  const [showDealerNotifyErrorToast, setShowDealerNotifyErrorToast] =
    useState(false);

  const {
    addEquipmentDrawer,
    closeAllDrawers,
    setIsLoading: setIsDrawerLoading,
    isLoading: isDrawerLoading
  } = useMyEquipmentDrawer() || {};
  const addEquipmentDrawerProps = {
    ...addEquipmentDrawer?.drawerProps,
    disableBackdropClick: addEquipmentDrawer?.isChild,
    hideCloseButton: addEquipmentDrawer?.isChild
  };

  const defaultFormValues = {
    serial: '',
    nickName: '',
    model: '',
    selectedEquipment: null,
    notifyDealer: false
  };
  const isSavedListDetails = pathname.includes(HEADLESS_SAVED_LISTS);
  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: defaultFormValues
  });
  const toast = useQuickToast();
  const { fireEvent } = useFormSubmitEvent();
  const addEquipmentData =
    isSavedListDetails && !defaultAddEquipment
      ? addEquipmentToList
      : addEquipment;

  const [state, formAction] = useFormState<
    AddEquipmentState,
    AddEquipmentDataForm
  >(addEquipmentData, null);
  const fireFormSubmissionGAEvents = (
    payload1: AnalyticsEventPayload,
    payload2: AnalyticsEventPayload
  ) => {
    fireEvent(payload1);
    fireEvent(payload2);
  };

  const getCurrentlyShoppingText = (selectedEquipmentValues: Equipment) => {
    const { model = '', equipmentFamily = '' } = selectedEquipmentValues || {};
    const equipmentModelFamilyName = model || equipmentFamily;
    return isSavedListDetails && !defaultAddEquipment
      ? t('EQUIP_ADDED_TO_LIST', { 0: equipmentModelFamilyName })
      : t('CURRENTLY_SHOPPING', {
          0: model,
          1: capitalizeText(equipmentFamily)
        });
  };

  const contentGuestToast = !isLoggedIn ? (
    <RegisterSignInButtonToast
      signInText={t('SIGN_IN')}
      registerText={t('SL_REGISTER')}
      guestSaveText={t('GUEST_EQUIPMENT_SAVE')}
    />
  ) : null;

  const showAddEquipmentToast = (selectedEquipmentValues: Equipment) => {
    addEquipmentDrawer?.isChild
      ? closeAllDrawers()
      : addEquipmentDrawer?.closeDrawer();

    toast.success({
      message: getCurrentlyShoppingText(selectedEquipmentValues),
      content: contentGuestToast,
      delay: 5
    });
  };

  const showFailToSendDealerEmailToast = () => {
    addEquipmentDrawer?.isChild
      ? closeAllDrawers()
      : addEquipmentDrawer?.closeDrawer();
    setShowDealerNotifyToasts(true);
    setShowAddEquipSuccessToast(true);
    setShowDealerNotifyErrorToast(true);
  };

  const addEquipmentWithRHFProps = () =>
    formAction.bind(null, {
      selectedEquipment: formMethods.getValues(SELECTED_EQUIPMENT),
      notifyDealer: formMethods.getValues(NOTIFY_DEALER),
      nickName: formMethods.getValues(FORM_FIELD_NICK_NAME),
      savedListId: params.savedListId || '',
      setAddToastCookie: isAddEquipmentToastCookie
    })();

  useEffect(() => {
    if (!state) return;
    if (state.status === 'success') {
      const selectedEquipment = formMethods.getValues(SELECTED_EQUIPMENT);
      showAddEquipmentToast(selectedEquipment);
      fireFormSubmissionGAEvents(
        {
          formName: SAVE_EQUIPMENT,
          formStatus: STATUS_SUCCESS,
          formContent: selectedEquipment.serialNumber,
          formContent2: `${selectedEquipment.model} ${selectedEquipment?.equipmentFamily}`,
          formLocation: null
        },
        {
          formName: FORM_SAVE_EQUIPMENT,
          formStatus: STATUS_SUCCESS,
          formContent: selectedEquipment.serialNumber,
          formContent2: `${selectedEquipment.model} ${selectedEquipment?.equipmentFamily}`
        }
      );
    }
    // this is to show duplicate Banner
    if (state.status === 'error' && 'type' in state.data) {
      const selectedEquipment = formMethods.getValues(SELECTED_EQUIPMENT);
      if (state.data.type === DUPLICATE_EQUIPMENT_ERROR) {
        updateFeedback(DUPLICATE_EQUIPMENT_ERROR);
      } else if (state.data.type === SHARE_EQUIPMENT_DEALER_EMAIL_ERROR) {
        showFailToSendDealerEmailToast();
      } else if (state.data.type === GENERIC_ERROR) {
        updateFeedback(UNKNOWN_ERROR_MESSAGE);
        fireFormSubmissionGAEvents(
          {
            formName: SAVE_EQUIPMENT,
            formStatus: STATUS_FAIL,
            formContent: selectedEquipment.serialNumber,
            formContent2: `${selectedEquipment.model} ${selectedEquipment?.equipmentFamily}`,
            formLocation: null
          },
          {
            formName: FORM_SAVE_EQUIPMENT,
            formStatus: STATUS_FAIL,
            formFieldCausingError: NETWORK_ERROR,
            formContent: selectedEquipment.serialNumber,
            formContent2: `${selectedEquipment.model} ${selectedEquipment?.equipmentFamily}`
          }
        );
      }
    }
    setIsDrawerLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    if (addEquipmentDrawer.isDrawerOpen) {
      formMethods.reset(defaultFormValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addEquipmentDrawer.isDrawerOpen]);

  const { handleSubmit } = formMethods;
  const onSubmitHandler = () => {
    if (isDrawerLoading) return;
    setIsDrawerLoading(true);
    addEquipmentWithRHFProps();
  };

  return (
    <>
      <Conditional test={showDealerNotifyToasts}>
        <DealerNotifyFailureToast
          showAddEquipSuccessToast={showAddEquipSuccessToast}
          showDealerNotifyErrorToast={showDealerNotifyErrorToast}
          setShowAddEquipSuccessToast={setShowAddEquipSuccessToast}
          setShowDealerNotifyErrorToast={setShowDealerNotifyErrorToast}
          storeId={storeId}
          currentlyShoppingText={getCurrentlyShoppingText(
            formMethods.getValues(SELECTED_EQUIPMENT)
          )}
          contentGuestToast={contentGuestToast}
        />
      </Conditional>
      <FormProvider {...formMethods}>
        <Conditional
          test={renderWithPortals}
          fallback={
            <form onSubmit={handleSubmit(onSubmitHandler)}>{children}</form>
          }
        >
          <Portal id={ROOT_PORTALS}>
            <form onSubmit={handleSubmit(onSubmitHandler)}>
              <Drawer {...addEquipmentDrawerProps}>{children}</Drawer>
            </form>
          </Portal>
        </Conditional>
      </FormProvider>
    </>
  );
};
